import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import type {
  AssetsReliabilityData,
  IdlingEvent,
  DownTimeAssetDetails,
} from './types';
import { truncateTo2dp } from 'utils/helpers/general';
import type { Asset } from 'types/asset.types';
import type { WorkOrdersSquare } from 'types/squares.type';
import { calculateMTBF, getAssetLevelMetrics } from './utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  data: AssetsReliabilityData[];
  setSelectedAsset: any;
  selectedAsset: string;
  assetList: Asset[];
  workOrders: WorkOrdersSquare[];
  startDate: Date;
  endDate: Date;
  idlingData: IdlingEvent[];
  downtimeData: DownTimeAssetDetails[];
  assetOrder: string[] | null;
  setAssetOrder: (arg: string[]) => void;
}

export const AssetChart = ({
  enableDarkTheme,
  data,
  setSelectedAsset,
  selectedAsset,
  assetList,
  workOrders,
  startDate,
  endDate,
  idlingData,
  downtimeData,
  assetOrder,
  setAssetOrder,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const mtbfResults = useMemo(() => {
    return calculateMTBF(workOrders);
  }, [workOrders]);

  const allowedIds = useMemo(
    () => assetList.map((item) => item.bumperNumber),
    [assetList]
  );

  const filteredData = useMemo(() => {
    return mtbfResults
      .filter((item) => allowedIds.includes(item.asset_id))
      .sort((a, b) => (a.mtbf_days ?? 0) - (b.mtbf_days ?? 0));
  }, [mtbfResults, allowedIds]);

  const fontColor = palette.textPrimary;

  const borderColor = enableDarkTheme ? '#2F445E' : '#E0E0E0';
  const bgColor = enableDarkTheme ? 'transparent' : '#fff';
  const hoverBgColor = enableDarkTheme
    ? `${palette.primary}1A`
    : `${palette.primary}0D`;

  const [orderBy, setOrderBy] = useState<string>('mtbf_days');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const withMetrics = useMemo(() => {
    return getAssetLevelMetrics(
      filteredData,
      startDate,
      endDate,
      workOrders,
      downtimeData,
      idlingData
    );
  }, [filteredData, startDate, endDate, workOrders, downtimeData, idlingData]);

  const comparator = useMemo(() => {
    return (a: any, b: any) => {
      let aValue: string | number = 0;
      let bValue: string | number = 0;

      switch (orderBy) {
        case 'asset_id':
          aValue = a.asset_id;
          bValue = b.asset_id;
          break;
        case 'mtbf_days':
          aValue = a.mtbf_days ?? 0;
          bValue = b.mtbf_days ?? 0;
          break;
        case 'failureRate':
          aValue = a.failureRate ?? 0;
          bValue = b.failureRate ?? 0;
          break;
        case 'availability':
          aValue = a.availability ?? 0;
          bValue = b.availability ?? 0;
          break;
        case 'utilization':
          aValue = a.utilization ?? 0;
          bValue = b.utilization ?? 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return order === 'asc'
        ? Number(aValue) - Number(bValue)
        : Number(bValue) - Number(aValue);
    };
  }, [order, orderBy]);

  const sortedData = useMemo(() => {
    return [...withMetrics].sort(comparator);
  }, [withMetrics, comparator]);

  useEffect(() => {
    const assetIds = sortedData.map((item) => item.asset_id);

    if (
      !assetOrder ||
      assetOrder.length !== assetIds.length ||
      !assetOrder.every((id, i) => id === assetIds[i])
    ) {
      setAssetOrder(assetIds);
    }
  }, [sortedData, assetOrder, setAssetOrder]);

  const maxMTBF = Math.max(...sortedData.map((item) => item.mtbf_days ?? 0));

  const handleRowClick = (assetId: string) => {
    setSelectedAsset(assetId);
  };

  return (
    <>
      {withMetrics.length === 0 ? (
        <Typography
          sx={{
            fontSize: '12px',
            color: fontColor,
          }}
        >
          {t('no_data_for_asset_level_mtbf')}
        </Typography>
      ) : (
        <Box
          sx={{
            border: `1px solid ${borderColor}`,
            padding: '10px',
            borderRadius: '4px',
          }}
        >
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: '600',
              color: fontColor,
              mb: 2,
            }}
          >
            {t('asset_level_mtbf')}
          </Typography>
          <TableContainer
            sx={{
              backgroundColor: bgColor,
              boxShadow: 'none',
              border: `1px solid ${borderColor}`,
              height: sortedData.length * 60 + 100,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      color: fontColor,
                      fontWeight: 600,
                      fontSize: '14px',
                      borderBottom: `1px solid ${borderColor}`,
                      width: '40px',
                      paddingRight: '5px',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'asset_id'}
                      direction={orderBy === 'asset_id' ? order : 'asc'}
                      onClick={() => {
                        handleSort('asset_id');
                      }}
                      sx={{
                        color: fontColor,
                        '&:hover': { color: fontColor },
                        '&.Mui-active': { color: fontColor },
                        '& .MuiTableSortLabel-icon': {
                          color: `${fontColor} !important`,
                        },
                      }}
                    >
                      {t('asset_id')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: fontColor,
                      fontWeight: 600,
                      fontSize: '14px',
                      borderBottom: `1px solid ${borderColor}`,
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'mtbf_days'}
                      direction={orderBy === 'mtbf_days' ? order : 'asc'}
                      onClick={() => {
                        handleSort('mtbf_days');
                      }}
                      sx={{
                        color: fontColor,
                        '&:hover': { color: fontColor },
                        '&.Mui-active': { color: fontColor },
                        '& .MuiTableSortLabel-icon': {
                          color: `${fontColor} !important`,
                        },
                      }}
                    >
                      {t('mtbf_days')}
                    </TableSortLabel>
                  </TableCell>
                  {/* <TableCell
                    sx={{
                      color: fontColor,
                      fontWeight: 600,
                      fontSize: '14px',
                      borderBottom: `1px solid ${borderColor}`,
                    }}
                    align="right"
                  >
                    <TableSortLabel
                      active={orderBy === 'failureRate'}
                      direction={orderBy === 'failureRate' ? order : 'asc'}
                      onClick={() => {
                        handleSort('failureRate');
                      }}
                      sx={{
                        color: fontColor,
                        '&:hover': { color: fontColor },
                        '&.Mui-active': { color: fontColor },
                        '& .MuiTableSortLabel-icon': {
                          color: `${fontColor} !important`,
                        },
                        flexDirection: 'row-reverse',
                      }}
                    >
                      Failure Rate
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: fontColor,
                      fontWeight: 600,
                      fontSize: '14px',
                      borderBottom: `1px solid ${borderColor}`,
                    }}
                    align="right"
                  >
                    <TableSortLabel
                      active={orderBy === 'availability'}
                      direction={orderBy === 'availability' ? order : 'asc'}
                      onClick={() => {
                        handleSort('availability');
                      }}
                      sx={{
                        color: fontColor,
                        '&:hover': { color: fontColor },
                        '&.Mui-active': { color: fontColor },
                        '& .MuiTableSortLabel-icon': {
                          color: `${fontColor} !important`,
                        },
                        flexDirection: 'row-reverse',
                      }}
                    >
                      Availability
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: fontColor,
                      fontWeight: 600,
                      fontSize: '14px',
                      borderBottom: `1px solid ${borderColor}`,
                    }}
                    align="right"
                  >
                    <TableSortLabel
                      active={orderBy === 'utilization'}
                      direction={orderBy === 'utilization' ? order : 'asc'}
                      onClick={() => {
                        handleSort('utilization');
                      }}
                      sx={{
                        color: fontColor,
                        '&:hover': { color: fontColor },
                        '&.Mui-active': { color: fontColor },
                        '& .MuiTableSortLabel-icon': {
                          color: `${fontColor} !important`,
                        },
                        flexDirection: 'row-reverse',
                      }}
                    >
                      Utilization
                    </TableSortLabel>
                  </TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((item) => {
                  const mtbfValue = item.mtbf_days ?? 0;
                  const barWidth =
                    maxMTBF > 0 ? (mtbfValue / maxMTBF) * 100 : 0;
                  const isSelected = selectedAsset === item.asset_id;

                  return (
                    <TableRow
                      key={item.asset_id}
                      onClick={() => {
                        handleRowClick(item.asset_id);
                      }}
                      sx={{
                        height: 60,
                        cursor: 'pointer',
                        backgroundColor: isSelected
                          ? hoverBgColor
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: hoverBgColor,
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          color: fontColor,
                          fontSize: '14px',
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        {item.asset_id}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              height: 25,
                              backgroundColor: enableDarkTheme
                                ? palette.paperBackground
                                : '#f5f5f5',
                              borderRadius: '2px',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                height: '100%',
                                width: `${barWidth}%`,
                                backgroundColor: palette.primary,
                                borderRadius: '0 2px 2px 0',
                                transition: 'width 0.3s ease',
                              }}
                            />
                            <Typography
                              sx={{
                                position: 'absolute',
                                left: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#fff',
                                fontSize: '12px',
                                fontWeight: 500,
                                zIndex: 1,
                              }}
                            >
                              {truncateTo2dp(item?.mtbf_days ?? 0)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {/* <TableCell
                        sx={{
                          color: fontColor,
                          fontSize: '14px',
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                        align="right"
                      >
                        {item.failureRate !== undefined
                          ? truncateTo2dp(item.failureRate)
                          : 'N/A'}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: fontColor,
                          fontSize: '14px',
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                        align="right"
                      >
                        {item.availability !== undefined
                          ? truncateTo2dp(item.availability)
                          : 'N/A'}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: fontColor,
                          fontSize: '14px',
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                        align="right"
                      >
                        {item.utilization !== undefined
                          ? truncateTo2dp(item.utilization)
                          : 'N/A'}
                      </TableCell> */}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </>
  );
};
